    import React, { useState, useEffect } from 'react';

export default function GuessTheNumber() {
  const [gameState, setGameState] = useState('menu');
  const [playerName, setPlayerName] = useState('');
  const [secretNumber, setSecretNumber] = useState(null);
  const [guess, setGuess] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState(''); // 'higher', 'lower', 'correct'
  const [ranking, setRanking] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [gameTime, setGameTime] = useState(0);

  const MIN_NUMBER = 1;
  const MAX_NUMBER = 100;

  // Timer del juego
  useEffect(() => {
    if (gameState === 'playing') {
      const timer = setInterval(() => setGameTime(t => t + 1), 1000);
      return () => clearInterval(timer);
    }
  }, [gameState]);

  const playSound = (type) => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (type === 'hint') {
        oscillator.frequency.value = 400;
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.15);
      } else if (type === 'correct') {
        oscillator.frequency.value = 800;
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      }
    } catch (e) {
      console.log('Audio no disponible');
    }
  };

  const startGame = () => {
    const random = Math.floor(Math.random() * (MAX_NUMBER - MIN_NUMBER + 1)) + MIN_NUMBER;
    setSecretNumber(random);
    setGameState('playing');
    setGuess('');
    setAttempts(0);
    setFeedback('');
    setFeedbackType('');
    setGameTime(0);
  };

  const handleGuess = () => {
    const inputNumber = parseInt(guess);

    if (isNaN(inputNumber) || inputNumber < MIN_NUMBER || inputNumber > MAX_NUMBER) {
      setFeedback(`Por favor ingresa un número entre ${MIN_NUMBER} y ${MAX_NUMBER}`);
      setFeedbackType('error');
      return;
    }

    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    if (inputNumber === secretNumber) {
      playSound('correct');
      setFeedback(`¡¡ACERTASTE!! El número era ${secretNumber}`);
      setFeedbackType('correct');
      saveScore(newAttempts);
      setTimeout(() => setGameState('victory'), 1500);
    } else if (inputNumber < secretNumber) {
      playSound('hint');
      setFeedback('El número es MAYOR ⬆️');
      setFeedbackType('higher');
    } else {
      playSound('hint');
      setFeedback('El número es MENOR ⬇️');
      setFeedbackType('lower');
    }

    setGuess('');
  };

  const saveScore = (finalAttempts) => {
    const newScore = {
      name: playerName || 'Anónimo',
      attempts: finalAttempts,
      time: gameTime,
      score: Math.max(1000 - (finalAttempts * 50) - gameTime, 0)
    };
    const updated = [...ranking, newScore].sort((a, b) => b.score - a.score).slice(0, 10);
    setRanking(updated);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Menú principal
  if (gameState === 'menu') {
    return (
      <div className="menu-container">
        <div className="menu-card">
          <h1 className="menu-title">🎯 Adivina el Número</h1>
          <p className="menu-subtitle">Adivina el número secreto entre 1 y 100</p>

          <div className="menu-form">
            <div className="form-group">
              <label>Tu nombre:</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Ingresa tu nombre"
                className="input-text"
              />
            </div>

            <div className="rules-box">
              <h3>Reglas del juego:</h3>
              <ul>
                <li>🔢 Piensa en un número entre 1 y 100</li>
                <li>💡 El juego te dará pistas (mayor/menor)</li>
                <li>⏱️ Intenta acertar en el menor tiempo posible</li>
                <li>🏆 Menos intentos = más puntos</li>
              </ul>
            </div>
          </div>

          <button onClick={startGame} className="btn-play">
            Jugar
          </button>

          {ranking.length > 0 && (
            <div className="ranking-section">
              <h2 className="ranking-title">🏆 Top 10 Ranking</h2>
              <div className="ranking-list">
                {ranking.map((score, idx) => (
                  <div key={idx} className="ranking-item">
                    <div className="ranking-left">
                      <span className="ranking-position">#{idx + 1}</span>
                      <span className="ranking-name">{score.name}</span>
                    </div>
                    <div className="ranking-right">
                      <span className="ranking-moves">{score.attempts} intentos</span>
                      <span className="ranking-time">{formatTime(score.time)}</span>
                      <span className="ranking-score">{score.score} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Pantalla de juego
  if (gameState === 'playing') {
    return (
      <div className="game-container">
        <div className="game-wrapper">
          {/* Header */}
          <div className="game-header">
            <h1 className="game-title">🎯 Adivina el Número</h1>
            <div className="game-stats">
              <div className="stat-box">
                <span className="stat-label">⏱️</span>
                <span className="stat-value">{formatTime(gameTime)}</span>
              </div>
              <div className="stat-box">
                <span className="stat-label">🎯</span>
                <span className="stat-value">{attempts}</span>
              </div>
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="btn-sound"
              >
                {soundEnabled ? '🔊' : '🔇'}
              </button>
            </div>
          </div>

          {/* Juego */}
          <div className="guess-game-container">
            <div className="guess-card">
              <div className="guess-instruction">
                <p>Adivina el número entre <strong>1</strong> y <strong>100</strong></p>
              </div>

              {/* Feedback */}
              {feedback && (
                <div className={`feedback feedback-${feedbackType}`}>
                  <p>{feedback}</p>
                </div>
              )}

              {/* Input y botón */}
              <div className="input-group">
                <input
                  type="number"
                  value={guess}
                  onChange={(e) => setGuess(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleGuess()}
                  placeholder="Ingresa tu número"
                  className="input-guess"
                  min={MIN_NUMBER}
                  max={MAX_NUMBER}
                  autoFocus
                />
                <button onClick={handleGuess} className="btn-guess">
                  Adivinar
                </button>
              </div>

              {/* Stats */}
              {attempts > 0 && (
                <div className="attempts-info">
                  <p>Intentos realizados: <strong>{attempts}</strong></p>
                  <p>Tiempo: <strong>{formatTime(gameTime)}</strong></p>
                </div>
              )}

              {/* Rango de ayuda */}
              {attempts > 2 && (
                <div className="hint-box">
                  <p>💡 Pista: Usa la información que recibiste para estrechar el rango</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pantalla de victoria
  if (gameState === 'victory') {
    const finalScore = Math.max(1000 - (attempts * 50) - gameTime, 0);
    return (
      <div className="victory-container">
        <div className="victory-card">
          <div className="victory-emoji">🎉</div>
          <h1 className="victory-title">¡Lo acertaste!</h1>
          <p className="victory-subtitle">¡Felicidades! Adivinaste el número</p>

          <div className="victory-stats">
            <div className="stat-row">
              <span className="stat-label">Intentos:</span>
              <span className="stat-value-large">{attempts}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Tiempo:</span>
              <span className="stat-value-large">{formatTime(gameTime)}</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-row">
              <span className="stat-label">Puntuación:</span>
              <span className="stat-value-score">{finalScore} pts</span>
            </div>
          </div>

          <div className="victory-buttons">
            <button
              onClick={() => setGameState('menu')}
              className="btn-menu"
            >
              Volver al menú
            </button>
            <button
              onClick={startGame}
              className="btn-again"
            >
              🔄 Jugar de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }
}